import React, {ReactNode} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {ROUTE} from 'core/routes';
import {
    Gate,
    listState$,
    filtersChanged,
    sortingsChanged,
    filtersReset,
    filtersSubmit,
    pageIncremented,
    updOnlyChanged,
} from 'models/documents/documents/grid/list';
import {DocumentsAccessors, DocumentsGridFormattedRow, DocumentsGridRawRow} from 'types';

import {FlexRowElement, ListRow, RowElement, Text} from './styled';

interface Props {
    configFunc: (t: TFunction, showType: boolean) => Array<any>;
    dataMapper: (
        data: DocumentsGridRawRow[] | undefined,
        t: TFunction,
    ) => DocumentsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
}

export const DocumentsGridList = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    updOnlyChanged(
        location.pathname === ROUTE.DOCUMENTS_INVOICES_INCOMING ||
            location.pathname === ROUTE.DOCUMENTS_INVOICES_OUTGOING ||
            location.pathname === ROUTE.DOCUMENTS_TRANSFER_INCOMING ||
            location.pathname === ROUTE.DOCUMENTS_TRANSFER_OUTGOING,
    );
    const {t} = useTranslation();
    const {data, filters, sortings, showConfigBlock, updOnly} = useStore(listState$);
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);

    return (
        <>
            <Gate />
            <EndlessList<DocumentsGridFormattedRow>
                rows={mappedData}
                hasMore={data?.hasNext}
                renderRow={(row: DocumentsGridFormattedRow) => (
                    <ListRow>
                        <FlexRowElement>
                            <Text>{row[DocumentsAccessors.createdDate]}</Text>
                            <Text isGray>{row[DocumentsAccessors.status]}</Text>
                        </FlexRowElement>
                        <RowElement>
                            <Text>{row[DocumentsAccessors.id]}</Text>
                        </RowElement>
                        {location.pathname === ROUTE.DOCUMENTS_INVOICES_INCOMING ||
                        location.pathname === ROUTE.DOCUMENTS_INVOICES_OUTGOING ||
                        location.pathname === ROUTE.DOCUMENTS_TRANSFER_INCOMING ||
                        location.pathname === ROUTE.DOCUMENTS_TRANSFER_OUTGOING ? (
                            <>
                                <FlexRowElement>
                                    <Text>{row[DocumentsAccessors.seller]}</Text>
                                </FlexRowElement>
                                <FlexRowElement>
                                    <Text>{row[DocumentsAccessors.buyer]}</Text>
                                </FlexRowElement>
                            </>
                        ) : (
                            <FlexRowElement>
                                <Text>{row[DocumentsAccessors.owner]}</Text>
                            </FlexRowElement>
                        )}
                        <FlexRowElement>
                            <Text isGray>{row[DocumentsAccessors.type]}</Text>
                        </FlexRowElement>
                    </ListRow>
                )}
                onNextPage={() => pageIncremented()}
                exportConfig={exportConfig}
                configurationBlock={mapListConfig(
                    configFunc(t, !updOnly),
                    filters,
                    sortings,
                    (value) => filtersChanged([value]),
                    filtersSubmit,
                    filtersReset,
                    sortingsChanged,
                    showConfigBlock,
                )}
                headerControls={headerControls}
            />
        </>
    );
};
